import { Directive, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output, output } from '@angular/core';

@Directive({
  selector: '[elementInViewport]',
  standalone: true
})
export class ElementInViewportDirective implements OnInit, OnDestroy {
  // Configuration inputs
  @Input() viewportMargin: string = '0px';
  @Input() viewportThreshold: number | number[] = 1.0;
  @Input() viewportRoot: Element | null = null;
  @Input() viewportDelay: number = 0;
  @Input() viewportObserveOnce: boolean = false;
  
  // Event outputs
  @Output() viewportVisibilityChange = new EventEmitter<boolean>();
  @Output() enteredViewport = new EventEmitter<void>();
  @Output() exitedViewport = new EventEmitter<void>();
  @Output() viewportVisibleRatio = new EventEmitter<number>();
  viewportEntryChange = output<IntersectionObserverEntry>();
  
  private observer: IntersectionObserver;
  private timeout: any;
  private hasBeenInViewport: boolean = false;

  constructor(private readonly elementRef: ElementRef) {}

  ngOnInit(): void {
    this.createObserver();
  }

  ngOnDestroy(): void {
    this.destroyObserver();
    if (this.timeout) {
      clearTimeout(this.timeout);
    }
  }

  private createObserver(): void {
    if (typeof IntersectionObserver === 'undefined') {
      console.warn('IntersectionObserver API is not available in this browser');
      return;
    }

    const options: IntersectionObserverInit = {
      root: this.viewportRoot,
      rootMargin: this.viewportMargin,
      threshold: this.viewportThreshold
    };

    this.observer = new IntersectionObserver(
      (entries) => this.handleIntersection(entries),
      options
    );

    this.observer.observe(this.elementRef.nativeElement);
  }

  stopObserving(): void {
    this.destroyObserver();
  }

  restartObserving(): void {
    this.destroyObserver();
    this.createObserver();
  }

  private destroyObserver(): void {
    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }
  }

  private handleIntersection(entries: IntersectionObserverEntry[]): void {
    const entry = entries[0];
    this.viewportEntryChange.emit(entry);
    const isInViewport = entry.isIntersecting;
    
    // Emit the intersection ratio
    this.viewportVisibleRatio.emit(entry.intersectionRatio);

    // Handle the once-only option
    if (this.viewportObserveOnce && isInViewport) {
      this.hasBeenInViewport = true;
      this.emitViewportEvents(isInViewport);
      this.destroyObserver();
      return;
    }
    
    // Skip if we've already processed this state with once-only
    if (this.viewportObserveOnce && this.hasBeenInViewport) {
      return;
    }

    // Clear any existing timeout
    if (this.timeout) {
      clearTimeout(this.timeout);
    }

    // Apply delay if specified
    if (this.viewportDelay > 0) {
      this.timeout = setTimeout(() => {
        this.emitViewportEvents(isInViewport);
      }, this.viewportDelay);
    } else {
      this.emitViewportEvents(isInViewport);
    }
  }

  private emitViewportEvents(isInViewport: boolean): void {
    this.viewportVisibilityChange.emit(isInViewport);
    
    if (isInViewport) {
      this.enteredViewport.emit();
    } else {
      this.exitedViewport.emit();
    }
  }
} 